import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useFormGuardStore } from '@/stores/form-guard.store';
import { isEmpty } from 'lodash';
import { TriangleAlert } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useBlocker } from 'react-router';

interface FormUnsaveChangeGuardProps {
  /**
   * Mark the form pristine as soon as its submit handler returns. Off for a
   * form that saves through a mutation and resets itself on success: the
   * handler returns before the request settles, so resetting here would clean
   * a form whose save then fails.
   */
  resetOnSubmit?: boolean;
}

function FormUnsaveChangeGuard({
  resetOnSubmit = true,
}: FormUnsaveChangeGuardProps = {}) {
  const form = useFormContext();
  const [open, setOpen] = useState(false);

  const ignoreGuard = useFormGuardStore(state => state.ignoreGuard);
  const resetGuard = useFormGuardStore(state => state.resetGuard);

  const blocker = useBlocker(
    !ignoreGuard && !isEmpty(form.formState.dirtyFields)
  );

  useEffect(() => {
    if (resetOnSubmit && form.formState.isSubmitSuccessful) {
      form.reset(form.getValues());
    }
  }, [resetOnSubmit, form.formState.isSubmitSuccessful, form]);

  useEffect(() => {
    if (blocker.state === 'blocked') {
      setOpen(true);
    }
  }, [blocker.state]);

  // `ignoreGuard` is a one-shot bypass: a caller sets it right before an
  // intentional navigation (save/archive/delete) so this guard doesn't
  // challenge it. Nothing else ever puts it back — `resetGuard` above only
  // runs from the dialog's own Proceed/Cancel, which by construction never
  // opens once the flag is set. Without this, the flag outlives the
  // navigation it was set for and silently disables the guard for every
  // form mounted afterwards, for the rest of the session. Resetting it here,
  // on unmount, self-clears the bypass once this form is actually left,
  // without touching the in-flight navigation that relied on it staying
  // `true` up to that point.
  useEffect(() => {
    return () => {
      resetGuard();
    };
  }, [resetGuard]);

  const handleProceed = () => {
    setOpen(false);
    resetGuard();
    blocker.proceed?.();
  };

  const handleCancel = () => {
    setOpen(false);
    resetGuard();
    blocker.reset?.();
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogContent>
        <AlertDialogHeader className="flex items-center gap-6">
          <TriangleAlert strokeWidth={1} className="size-8" />
          <div className="flex flex-col gap-2">
            <AlertDialogTitle className="text-text-primary text-center text-lg font-semibold">
              Exit Without Saving?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-text-tertiary text-center text-sm">
              You’ve made changes that haven’t been saved. Are you sure you want
              to exit?
            </AlertDialogDescription>
          </div>
        </AlertDialogHeader>

        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleCancel}>
            Keep Editing
          </AlertDialogCancel>
          <AlertDialogAction onClick={handleProceed}>
            Exit Anyway
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
export { FormUnsaveChangeGuard };
